/**
 * The app's one button: shadcn's Button, with its variant, size and asChild
 * props as generated; its looks live in styles/variants (buttonVariants).
 *
 * There used to be two copies of this, this and src/components/ui/button, with
 * different base classes (the other had a gap of its own, forced icon sizes and
 * a filled outline). The classes kept are these, because seventy files were
 * written against them; the five photo dialogs and the calendar that used the
 * other now use this and buttonVariants.
 *
 * Used by: nearly every screen's buttons, and the photo dialogs
 * (features/plant-list/components/dialogs/photo).
 *
 * Loading (B111, owner decision Q3): `loading` swaps the label for a spinner and
 * the waiting word (`busy`: sitewide.saving by default, or .deleting, .sending, .adding, .archiving, .uploading, .opening; the map is here only), disables the button and sets aria-busy, so it cannot
 * be tapped twice; the size and variant classes stay, and a minimum width (min-w-28) holds the width so the button, and the Cancel beside it, do not jump
 * between a short label and "Saving...". It is not drawn with asChild,
 * where the child is the element and has no label of ours to swap.
 *
 * @module button
 */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import type { VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { buttonVariants } from "../../styles/variants"
import Spinner from "./Spinner"
import { useTranslation } from "@/shared/hooks/useTranslation"

/** What a waiting button is doing; each value has one waiting word in the locale files (WAITING_KEYS). */
export type ButtonBusy = 'saving' | 'deleting' | 'sending' | 'adding' | 'archiving' | 'uploading' | 'opening'

/** The one place a waiting word is chosen: the sitewide key each ButtonBusy shows. No screen writes a waiting word. */
const WAITING_KEYS: Record<ButtonBusy, string> = {
  saving: 'sitewide.saving',
  deleting: 'sitewide.deleting',
  sending: 'sitewide.sending',
  adding: 'sitewide.adding',
  archiving: 'sitewide.archiving',
  uploading: 'sitewide.uploading',
  opening: 'sitewide.opening',
}

/** A native button's props, a variant and size, and asChild to style a child instead. */
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the one child (a Link, say) with the button's classes, instead of a <button>. */
  asChild?: boolean
  /** Waiting on the server: shows a spinner and the sitewide.saving word, disables the button, sets aria-busy. */
  loading?: boolean
  /** The waiting word shown while `loading`: saving (default), deleting, sending, adding, archiving, uploading or opening (a button that leaves for another site). */
  busy?: ButtonBusy
}

/** A button in one of the app's variants and sizes; asChild lends the look to its child (a link, say). */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, busy: doing = 'saving', disabled, children, ...props }, ref) => {
    const { reactTranslate: t } = useTranslation()
    const Comp = asChild ? Slot : "button"
    const busy = loading && !asChild
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), busy && "min-w-28 gap-2")}
        ref={ref}
        disabled={busy ? true : disabled}
        aria-busy={busy ? true : undefined}
        {...props}
      >
        {busy ? (
          <>
            <Spinner size={14} color="currentColor" />
            {t(WAITING_KEYS[doing])}
          </>
        ) : children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button }
